/**
 * Secure token storage utility for managing authentication tokens
 * Uses localStorage with proper error handling and fallbacks
 */

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export const tokenStorage = {
  /**npm
   * Store both access and refresh tokens
   */
  setTokens: (tokens: TokenPair): void => {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    } catch (error) {
      console.error("Failed to store tokens:", error);
      // Clear any partial data
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  },

  /**
   * Get the access token
   */
  getAccessToken: (): string | null => {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get access token:", error);
      return null;
    }
  },

  /**
   * Get the refresh token
   */
  getRefreshToken: (): string | null => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get refresh token:", error);
      return null;
    }
  },

  /**
   * Update only the access token (after refresh)
   */
  updateAccessToken: (accessToken: string): void => {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    } catch (error) {
      console.error("Failed to update access token:", error);
    }
  },

  /**
   * Clear all tokens
   */
  clearTokens: (): void => {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to clear tokens:", error);
    }
  },

  /**
   * Check if tokens exist
   */
  hasTokens: (): boolean => {
    try {
      return !!(
        localStorage.getItem(ACCESS_TOKEN_KEY) &&
        localStorage.getItem(REFRESH_TOKEN_KEY)
      );
    } catch (error) {
      console.error("Failed to check tokens:", error);
      return false;
    }
  },

  /**
   * Get both tokens as a pair
   */
  getTokenPair: (): TokenPair | null => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (accessToken && refreshToken) {
        return { accessToken, refreshToken };
      }
      return null;
    } catch (error) {
      console.error("Failed to get token pair:", error);
      return null;
    }
  },
};
